import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  IQueueItem,
  Ag1QueueItemComponent,
} from '../ag1-queue-item/ag1-queue-item.component';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1LinkComponent } from '../ag1-link/ag1-link.component';

@Component({
  selector: 'ag1-queue-indicator',
  templateUrl: './ag1-queue-indicator.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Bind,
    Button,
    Tooltip,
    AngularSvgIconModule,
    Ag1LinkComponent,
    Ag1QueueItemComponent,
  ],
})
export class Ag1QueueIndicatorComponent {
  /** (Required) The queue items to display in the queue indicator. 
   * Example of a queue item:
   * {
      id: '1',
      title: 'Queue Item 1',
      subTitle: 'Queue Item 1 Subtitle',
      state: 'loading',
      leadingIcon: {
        iconUrl: 'assets/icons/general/document-no-text.svg',
        color: 'text-steel-blue-500',
      },
      action: {
        label: 'link',
      },
    },
  */
  @Input()
  set queueItems(items: IQueueItem[]) {
    this._queueItems = items;

    // Auto-collapse when queue is empty
    if (items?.length === 0 && this.isExpanded) {
      this.collapseQueueIndicator();
    }
  }

  /** (Optional) The title to display in the queue indicator. */
  @Input() title: string = 'Title';

  /** (Optional) Whether the completion bar is visible or not. */
  @Input() hasCompletionBar: boolean = true;

  /** (Optional) The event to emit when the queue indicator is opened. */
  @Output() openEvent = new EventEmitter<void>();

  /** (Optional) The event to emit when the queue indicator is closed. */
  @Output() closeEvent = new EventEmitter<void>();

  /** (Optional) The event to emit when the view button is clicked. */
  @Output() viewClickEvent = new EventEmitter<void>();

  /** (Optional) The event to emit when an item is clicked in the queue indicator. */
  @Output() itemClickEvent = new EventEmitter<IQueueItem>();

  private _queueItems: IQueueItem[] = [];

  isExpanded: boolean = false;
  isOpen: boolean = false;

  onToggleQueueIndicator(): void {
    this.isExpanded = !this.isExpanded;
  }

  public openQueueIndicator(): void {
    if (this.isOpen) return;

    this.isOpen = true;

    this.openEvent.emit();
  }

  public closeQueueIndicator(): void {
    if (!this.isOpen) return;

    this.isOpen = false;

    this.closeEvent.emit();
  }

  public expandQueueIndicator(): void {
    if (this.isExpanded) return;
    this.isExpanded = true;
  }

  public collapseQueueIndicator(): void {
    if (!this.isExpanded) return;
    this.isExpanded = false;
  }

  onCloseEvent(): void {
    this.closeQueueIndicator();
  }

  onViewClick(): void {
    this.viewClickEvent.emit();
  }

  onItemClick(item: IQueueItem): void {
    this.itemClickEvent.emit(item);
  }

  get queueItemsCount(): number {
    return this.queueItems?.length || 0;
  }

  get successQueueItemsCount(): number {
    return (
      this.queueItems.filter((item) => item.state === 'success').length || 0
    );
  }

  get queueItems(): IQueueItem[] {
    return this._queueItems;
  }
}
